import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, ChevronsUpDown, Check, X, Plus, Pencil, Trash2, ArrowLeft, Loader2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { notesApi } from '@/lib/notesApi';

// Shared palette — mirrors the backend _GROUP_COLORS for the Tasks groups.
const GROUP_COLORS = ['#B49BE0', '#7FB5A6', '#E0A45E', '#6C8FD1', '#D98BA8', '#8AAE68'];
const uniq = (arr) => Array.from(new Set(arr));

/*
 * Reusable "people + groups" picker used for sharing events / goals and
 * inviting meeting attendees. Groups are the shared Tasks people-groups
 * (`/notes/tasks/groups`). Selecting a group expands its members into the
 * recipient list as a SNAPSHOT (no live link). Users can create / edit /
 * delete their own groups right inside the popover.
 *
 * Props:
 *   value        string[]  selected user ids
 *   onChange     (ids) => void
 *   directory    [{id,name,email}]  selectable users (excludes the viewer)
 *   testidPrefix string    base for data-testid attributes
 *   renderLabel  (count) => string  optional trigger label
 *   emptyLabel   string    trigger label when nothing is selected
 */
export const GroupPeoplePicker = ({
  value = [],
  onChange,
  directory = [],
  testidPrefix = 'people-picker',
  triggerIcon: TriggerIcon = Users,
  renderLabel,
  emptyLabel = 'Add people',
  align = 'start',
}) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState('pick'); // 'pick' | 'form'
  const [editing, setEditing] = useState(null); // group being edited, or null for create
  const [fName, setFName] = useState('');
  const [fColor, setFColor] = useState(GROUP_COLORS[0]);
  const [fMembers, setFMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const dirMap = useMemo(() => Object.fromEntries((directory || []).map((u) => [u.id, u])), [directory]);
  const dirIds = useMemo(() => new Set((directory || []).map((u) => u.id)), [directory]);

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/tasks/groups');
      setGroups(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (open) loadGroups(); }, [open, loadGroups]);

  const toggleUser = (id) => {
    if (!onChange) return;
    onChange(value.includes(id) ? value.filter((x) => x !== id) : uniq([...value, id]));
  };

  const addGroup = (g) => {
    const add = (g.member_ids || []).filter((id) => dirIds.has(id));
    const before = value.length;
    const next = uniq([...value, ...add]);
    onChange && onChange(next);
    const added = next.length - before;
    if (added > 0) toast.success(`Added ${added} ${added === 1 ? 'person' : 'people'} from ${g.name}`);
    else toast.info(`Everyone in ${g.name} is already selected`);
  };

  const openCreate = () => {
    setEditing(null);
    setFName('');
    setFColor(GROUP_COLORS[0]);
    setFMembers(value.filter((id) => dirIds.has(id))); // prefill from current selection
    setMemberQuery('');
    setMode('form');
  };
  const openEdit = (g) => {
    setEditing(g);
    setFName(g.name || '');
    setFColor(g.color || GROUP_COLORS[0]);
    setFMembers((g.member_ids || []).filter((id) => dirIds.has(id)));
    setMemberQuery('');
    setMode('form');
  };

  const toggleFMember = (id) => setFMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const memberList = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return directory || [];
    return (directory || []).filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q));
  }, [directory, memberQuery]);

  const saveForm = async () => {
    const name = fName.trim();
    if (!name) { toast.error('Give the group a name'); return; }
    setSaving(true);
    try {
      const body = { name, color: fColor, member_ids: fMembers };
      if (editing) await notesApi.patch(`/notes/tasks/groups/${editing.id}`, body);
      else await notesApi.post('/notes/tasks/groups', body);
      toast.success(editing ? 'Group updated' : `Group "${name}" created`);
      await loadGroups();
      setMode('pick');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save group');
    } finally { setSaving(false); }
  };

  const deleteGroup = async (g) => {
    try {
      await notesApi.delete(`/notes/tasks/groups/${g.id}`);
      toast.success('Group deleted');
      await loadGroups();
    } catch { toast.error('Could not delete group'); }
  };

  const label = renderLabel ? renderLabel(value.length) : (value.length ? `${value.length} selected` : emptyLabel);

  return (
    <div>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setMode('pick'); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testidPrefix}-select`}
            className="n-focus mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-[var(--p-border)] bg-[var(--p-card)] px-3 py-2 text-left text-[13px] text-[color:var(--p-muted)] hover:border-[var(--p-accent)]"
          >
            <span className="flex items-center gap-1.5"><TriggerIcon size={14} /> {label}</span>
            <ChevronsUpDown size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="notes-scope w-[360px] p-0" align={align} data-testid={`${testidPrefix}-popover`}>
          {mode === 'pick' ? (
            <Command>
              <CommandInput placeholder="Search people or groups…" />
              <CommandList className="max-h-72">
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup heading="Groups">
                  {groups.map((g) => (
                    <CommandItem
                      key={g.id}
                      value={`group ${g.name}`}
                      onSelect={() => addGroup(g)}
                      data-testid={`${testidPrefix}-group-${g.id}`}
                    >
                      <span className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: g.color || GROUP_COLORS[0] }} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] text-[color:var(--p-text)]">{g.name}</span>
                        <span className="block truncate text-[11px] text-[color:var(--p-muted)]">
                          {g.member_count} {g.member_count === 1 ? 'member' : 'members'}
                        </span>
                      </span>
                      {g.is_owner && (
                        <span className="ml-1 flex shrink-0 items-center gap-0.5">
                          <button
                            type="button" aria-label="Edit group"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); openEdit(g); }}
                            data-testid={`${testidPrefix}-group-edit-${g.id}`}
                            className="n-focus grid h-6 w-6 place-items-center rounded text-[color:var(--p-muted)] hover:bg-[var(--p-accent-bg)] hover:text-[color:var(--p-accent)]"
                          ><Pencil size={12} /></button>
                          <button
                            type="button" aria-label="Delete group"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); deleteGroup(g); }}
                            data-testid={`${testidPrefix}-group-delete-${g.id}`}
                            className="n-focus grid h-6 w-6 place-items-center rounded text-[color:var(--p-muted)] hover:bg-[var(--p-accent-bg)] hover:text-[color:var(--p-danger)]"
                          ><Trash2 size={12} /></button>
                        </span>
                      )}
                    </CommandItem>
                  ))}
                  <CommandItem value="new group create" onSelect={openCreate} data-testid={`${testidPrefix}-new-group`}>
                    <Plus size={14} className="mr-2 text-[color:var(--p-accent)]" />
                    <span className="text-[13px] font-medium text-[color:var(--p-accent)]">
                      New group{value.length ? ' from selection' : ''}
                    </span>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading="People">
                  {(directory || []).map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.email}`}
                      onSelect={() => toggleUser(u.id)}
                      data-testid={`${testidPrefix}-option-${u.id}`}
                    >
                      <Check size={14} className={`mr-2 ${value.includes(u.id) ? 'opacity-100 text-[color:var(--p-accent)]' : 'opacity-0'}`} />
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] text-[color:var(--p-text)]">{u.name}</span>
                        <span className="block truncate text-[11px] text-[color:var(--p-muted)]">{u.email}</span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <div className="p-3" data-testid={`${testidPrefix}-group-form`}>
              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button" onClick={() => setMode('pick')} aria-label="Back"
                  className="n-focus grid h-7 w-7 place-items-center rounded-md border border-[var(--p-border)] text-[color:var(--p-muted)] hover:border-[var(--p-accent)]"
                ><ArrowLeft size={14} /></button>
                <span className="text-[13px] font-semibold text-[color:var(--p-text)]">{editing ? 'Edit group' : 'New group'}</span>
              </div>
              <Input
                value={fName} onChange={(e) => setFName(e.target.value)}
                placeholder="Group name (e.g. Sales team)" maxLength={80}
                data-testid={`${testidPrefix}-group-name-input`} className="mb-2"
              />
              <div className="mb-2 flex items-center gap-1.5">
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c} type="button" onClick={() => setFColor(c)} aria-label={`Colour ${c}`}
                    className={`h-6 w-6 rounded-full border-2 transition-transform ${fColor === c ? 'scale-110 border-[color:var(--p-text)]' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`${testidPrefix}-group-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--p-muted)]">Members</span>
                <span className="text-[11px] text-[color:var(--p-muted-2)]" data-testid={`${testidPrefix}-group-member-count`}>{fMembers.length} selected</span>
              </div>
              <div className="relative mb-1.5">
                <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--p-muted-2)]" />
                <Input
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Search people…"
                  className="h-8 pl-8 text-[12.5px]"
                  data-testid={`${testidPrefix}-group-member-search`}
                />
              </div>
              <div className="max-h-40 overflow-auto rounded-md border border-[var(--p-border)]">
                {memberList.length === 0 ? (
                  <div className="px-2.5 py-3 text-center text-[12px] text-[color:var(--p-muted)]">
                    {(directory || []).length === 0 ? 'No other users yet.' : 'No people match your search.'}
                  </div>
                ) : memberList.map((u) => (
                  <button
                    key={u.id} type="button" onClick={() => toggleFMember(u.id)}
                    data-testid={`${testidPrefix}-group-member-${u.id}`}
                    className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-[var(--p-accent-bg)]"
                  >
                    <Check size={14} className={`${fMembers.includes(u.id) ? 'opacity-100 text-[color:var(--p-accent)]' : 'opacity-0'}`} />
                    <span className="min-w-0">
                      <span className="block truncate text-[12.5px] text-[color:var(--p-text)]">{u.name}</span>
                      <span className="block truncate text-[10.5px] text-[color:var(--p-muted)]">{u.email}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                {editing ? (
                  <Button
                    type="button" variant="ghost"
                    onClick={() => { deleteGroup(editing); setMode('pick'); }}
                    className="text-[color:var(--p-danger)] hover:bg-[var(--p-accent-bg)]"
                    data-testid={`${testidPrefix}-group-form-delete`}
                  ><Trash2 size={14} className="mr-1" /> Delete</Button>
                ) : <span />}
                <Button
                  type="button" onClick={saveForm} disabled={saving}
                  className="bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]"
                  data-testid={`${testidPrefix}-group-form-save`}
                >{saving && <Loader2 size={14} className="mr-1 animate-spin" />} {editing ? 'Save' : 'Create'}</Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--p-border)] bg-[var(--p-accent-bg)] px-2 py-0.5 text-[11.5px] text-[color:var(--p-text-soft)]"
              data-testid={`${testidPrefix}-chip-${id}`}
            >
              {dirMap[id]?.name || 'User'}
              <button type="button" onClick={() => toggleUser(id)} className="hover:text-[color:var(--p-danger)]" aria-label="Remove person"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupPeoplePicker;
